import React, { useEffect, useState } from 'react';

import moment from 'moment-timezone';
import { useForm } from 'react-hook-form';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { sortAlphabeticalArray } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useQueryState } from 'hooks/useQueryState';

import { useCommentsEditorStatus } from 'store/commentsEditorStatus.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useMutationCreateCard } from 'api/mutations/createCard.mutation';
import { useMutationGetActivityOptions } from 'api/mutations/getActivityOptions.mutation';
import { useMutationGetWBSOptions } from 'api/mutations/getWBSOptions.mutation';
import { useQueryActivityDetailsByTaskdId } from 'api/queries/activityDetailsByTaskdId.query';
import { useQueryGetBoardInitLite } from 'api/queries/getBoardInitLite.query';

import {
    IActiveTab,
    TabsSection,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/TabsSection/TabsSection';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.module.scss';
import {
    IFields,
    IInitialCard,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.types';
import {
    buildCreateCardPayload,
    tabsArrData,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.utils';
import { CardContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/components/CardContent/CardContent';
import { CardHeader } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/components/CardHeader/CardHeader';
import { CreateCardStatusSelect } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/components/CreateCardStatusSelect/CreateCardStatusSelect';
import { mapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import {
    statusOptions,
    versionMapper,
    wbsMapper,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';

interface ICreateCardProps {
    saveCardCallback: (value?: number) => void;
    initialCard?: IInitialCard;
}

export const CreateCardModal = ({ saveCardCallback, initialCard }: ICreateCardProps) => {
    const [activeTab, setActiveTab] = useState<IActiveTab>('cardDetails');
    const [showModal, setShowModal] = useState(true);
    const history = useHistory();
    const { path } = useRouteMatch();
    const { pathname, search } = useLocation();
    const currentVersion = useVersionStore((state) => state.version);
    const latestVersionId = currentVersion?.id;
    const params = useParams<{ id: string }>();
    const { project, contract } = useProjectTreeStore();
    const { data: boardInit } = useQueryGetBoardInitLite({ projectId: project?.id });
    const { mutate: mutateWBSOptions } = useMutationGetWBSOptions();
    const { mutate: mutateActivityOptions } = useMutationGetActivityOptions();
    const { mutate: mutateCreateCard } = useMutationCreateCard();

    const { handleSnackBar } = useCustomSnackBar();

    const [taskId] = useQueryState('taskId');
    const { data: activityDetails } = useQueryActivityDetailsByTaskdId(taskId);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm<IFields>({
        defaultValues: {
            id: null,
            project: null,
            contract: null,
            versionName: null,
            wbsValue: null,
            activityId: null,
            title: '',
            status: statusOptions[0],
            details: '',
            mentionedList: [],
            tagList: [],
            assignee: null,
            category: null,
            isImportant: true,
            startDate: moment().valueOf(),
            endDate: null,
            externalLinks: [],

            relatedCardIds: null,
            type: 'USER_GENERATED',

            // dates by activity id
            cardDates: {
                plannedStartDate: undefined,
                plannedFinishDate: undefined,
                calculatedStartDate: undefined,
                calculatedFinishDate: undefined,
            },

            // fieldsOptions
            projectOptions: [],
            contractOptions: [],
            versionsOptions: [],
            wbsListOptions: [],
            activityListOptions: [],
            versionId: null,
        },
    });

    useEffect(() => {
        if (boardInit && project && contract && latestVersionId) {
            setValue('versionId', latestVersionId);
            // project
            const currentProject = boardInit.projectContractVersions.find((item) => item.id === project.id);
            setValue('project', mapper(currentProject));
            setValue('projectOptions', boardInit.projectContractVersions.map(mapper));

            // contract
            const contractOptions = currentProject?.contracts;
            const currentContract = contractOptions?.find((item) => item.id === contract.id);
            setValue('contract', mapper(currentContract));
            setValue('contractOptions', contractOptions ? contractOptions.map(mapper) : []);

            // versionName
            const versionOptions = currentContract?.metaDataResponse;
            const currentVersion = currentContract?.metaDataResponse.find((item) => item.id === latestVersionId);
            setValue('versionName', currentVersion ? versionMapper(currentVersion) : null);
            setValue('versionsOptions', versionOptions ? versionOptions.map(versionMapper) : []);

            // category
            const category = boardInit?.category.find((i) => i.name === 'USER_ACTION');
            setValue('category', category ? mapper(category) : null);

            // WBS List Options
            if (currentVersion?.id) {
                mutateWBSOptions(
                    { versionId: currentVersion.id },
                    {
                        onSuccess: (res) => {
                            setValue('wbsListOptions', sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper));
                        },
                    },
                );
            }
        }
    }, [boardInit, project, contract, latestVersionId]);

    // fill Fields by activity details
    useEffect(() => {
        if (boardInit && activityDetails) {
            // project
            const currentProject = boardInit.projectContractVersions.find(
                (item) => item.id === activityDetails.projectId,
            );
            setValue('project', currentProject ? mapper(currentProject) : null);
            setValue('projectOptions', boardInit.projectContractVersions.map(mapper));

            // contract
            const contractOptions = currentProject?.contracts;
            const currentContract = contractOptions?.find((item) => item.id === activityDetails.subProgramId);
            setValue('contract', currentContract ? mapper(currentContract) : null);
            setValue('contractOptions', contractOptions ? contractOptions.map(mapper) : []);

            // versionName
            const versionOptions = currentContract?.metaDataResponse;
            const currentVersion = versionOptions?.find((item) => item.id === activityDetails.versionId);
            setValue('versionName', currentVersion ? versionMapper(currentVersion) : null);
            setValue('versionsOptions', versionOptions ? versionOptions.map(versionMapper) : []);

            // WBS List Options
            if (currentVersion?.id) {
                mutateWBSOptions(
                    { versionId: currentVersion.id },
                    {
                        onSuccess: (res) => {
                            const wbsListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper);
                            const currentWbsValue = wbsListOptions?.find((item) => item.id === activityDetails.wbsId);
                            setValue('wbsValue', currentWbsValue || null);
                            setValue('wbsListOptions', wbsListOptions);
                        },
                    },
                );
            }

            mutateActivityOptions(
                { wbsId: activityDetails.wbsId },
                {
                    onSuccess: (res) => {
                        const activityListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper);
                        const currentActivityId = activityListOptions?.find(
                            (item) => item.id === activityDetails.activityId,
                        );
                        setValue('activityId', currentActivityId || null);
                        setValue('activityListOptions', activityListOptions);
                    },
                },
            );
        }
    }, [boardInit, activityDetails]);

    // fill Fields by initialCard
    useEffect(() => {
        if (initialCard) {
            setValue('title', initialCard.title);
            setValue('details', initialCard.details);
            setValue('category', mapper(initialCard.category));
            setValue('startDate', initialCard.startDate);
            setValue('endDate', initialCard.startDate);
            setValue('assignee', {
                id: null,
                value: 'Unassigned',
                label: 'Unassigned',
                assignee: {
                    id: null,
                    userName: 'Unassigned',
                    firstName: 'Unassigned',
                    lastName: '',
                    imageName: '',
                },
            });
        }
    }, [initialCard]);

    const handleCreate = (data) => {
        const payload = buildCreateCardPayload(data);

        mutateCreateCard(payload, {
            onSuccess: (res) => {
                handleSnackBar('Card Successfully Created', 'success');
                saveCardCallback(res.id);
                const link = `${generatePath(path, { ...params, cardId: res.id })}${search}`;
                history.push(link);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const handleSubmitError = () => {
        handleSnackBar('Please fill all mandatory fields', 'error');
    };

    //==================================== handleModalClose ============================================================
    const { resetCommentsEditorStatus } = useCommentsEditorStatus();
    const handleModalClose = () => {
        resetCommentsEditorStatus();
        // remove taskid on close card, so it won't affect other cards creation
        const queryParams = new URLSearchParams(search);
        if (queryParams.has('taskId')) {
            queryParams.delete('taskId');
        }

        setShowModal(false);
        const pathWithoutParams = pathname.slice(0, pathname.lastIndexOf('/'));
        history.push({
            pathname: pathWithoutParams,
            search: `?${queryParams.toString().replace(/%2C/g, ',')}`,
        });
    };
    //====================================================================================================

    return (
        <Modal
            open={showModal}
            onClose={handleModalClose}
            width={'140rem'}
            title={'User Action'}
            actions={
                <>
                    <Button
                        size={'large'}
                        variant={'secondary'}
                        onClick={handleModalClose}
                        id={'cardModalCloseOrCancelButton'}
                    >
                        Cancel
                    </Button>
                    <Button
                        form={'create-card-modal-form'}
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        id={'cardModalSaveButton'}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <form
                id={'create-card-modal-form'}
                onSubmit={handleSubmit(handleCreate, handleSubmitError)}
                className={classes.createCardContainer}
            >
                <CardHeader control={control} setValue={setValue} watch={watch} errors={errors} />
                <div className={classes.tabsWithStatusWrapper}>
                    <TabsSection
                        tabs={tabsArrData}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        wrapperClass={classes.tabWrapperClass}
                    />
                    <CreateCardStatusSelect control={control} />
                </div>

                <CardContent
                    boardInit={boardInit}
                    setValue={setValue}
                    watch={watch}
                    control={control}
                    errors={errors}
                />
            </form>
        </Modal>
    );
};
